import { useEffect, useState } from 'react';
import { apiFetch, apiPost } from '../../api/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface BusinessCenter {
  bc_id: string;
  bc_name: string;
}

interface Advertiser {
  advertiser_id: string;
  advertiser_name: string;
  selected?: boolean;
}

interface Asset {
  asset_id: string;
  asset_name: string;
  asset_type: 'CATALOG' | 'PIXEL' | 'TT_ACCOUNT' | string;
}

type SelectedAsset = Asset & { advertiser_id: string };

interface TikTokAccountSelectorProps {
  onClose: () => void;
  onSaved: () => void;
}

const STEP_TITLES = ['Select Business Center', 'Select Advertisers', 'Select Assets'];

const assetKey = (a: Asset) => `${a.asset_type}-${a.asset_id}`;

/**
 * TikTok connect wizard (mirrors mvp's TikTokAccountSelector): three steps —
 * Business Centers → Advertisers → Assets (catalogs / pixels / TikTok
 * accounts, optional with Skip). Unlike mvp, an empty or failed BC list does
 * not dead-end the flow: the wizard moves on to advertisers, which is the
 * data the audit actually needs.
 */
export default function TikTokAccountSelector({ onClose, onSaved }: TikTokAccountSelectorProps) {
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Business Centers
  const [businessCenters, setBusinessCenters] = useState<BusinessCenter[]>([]);
  const [loadingBcs, setLoadingBcs] = useState(true);
  const [selectedBcIds, setSelectedBcIds] = useState<string[]>([]);

  // Step 2: Advertisers
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [loadingAdvertisers, setLoadingAdvertisers] = useState(false);
  const [advertiserError, setAdvertiserError] = useState<string | null>(null);
  const [selectedAdvertiserIds, setSelectedAdvertiserIds] = useState<string[]>([]);

  // Step 3: Assets
  const [assets, setAssets] = useState<{ catalogs: Asset[]; pixels: Asset[]; ttAccounts: Asset[] }>(
    { catalogs: [], pixels: [], ttAccounts: [] },
  );
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<SelectedAsset[]>([]);
  const [assetFetchError, setAssetFetchError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Fetch Business Centers on mount
  useEffect(() => {
    let cancelled = false;
    apiFetch<{ businessCenters: BusinessCenter[]; error?: string }>('/api/tiktok/business-centers')
      .then((data) => {
        if (cancelled) return;
        setBusinessCenters(data.businessCenters || []);
      })
      .catch(() => {
        // BC listing is best-effort — the wizard falls through to advertisers.
      })
      .finally(() => {
        if (!cancelled) setLoadingBcs(false);
      });
    return () => { cancelled = true; };
  }, []);

  // --- Step 1: BC handlers ---
  const handleBcToggle = (bcId: string) => {
    setSelectedBcIds((prev) =>
      prev.includes(bcId) ? prev.filter((id) => id !== bcId) : [...prev, bcId],
    );
  };

  const handleSelectAllBcs = () => {
    if (selectedBcIds.length === businessCenters.length) {
      setSelectedBcIds([]);
    } else {
      setSelectedBcIds(businessCenters.map((bc) => bc.bc_id));
    }
  };

  // --- Step 2: Advertiser handlers ---
  const handleAdvertiserToggle = (advId: string) => {
    setSelectedAdvertiserIds((prev) =>
      prev.includes(advId) ? prev.filter((id) => id !== advId) : [...prev, advId],
    );
  };

  const handleSelectAllAdvertisers = () => {
    if (selectedAdvertiserIds.length === advertisers.length) {
      setSelectedAdvertiserIds([]);
    } else {
      setSelectedAdvertiserIds(advertisers.map((a) => a.advertiser_id));
    }
  };

  // --- Step 3: Asset handlers ---
  const isAssetSelected = (asset: Asset) =>
    selectedAssets.some((a) => assetKey(a) === assetKey(asset));

  const handleAssetToggle = (asset: Asset) => {
    setSelectedAssets((prev) => {
      if (prev.some((a) => assetKey(a) === assetKey(asset))) {
        return prev.filter((a) => assetKey(a) !== assetKey(asset));
      }
      // Associate with first selected advertiser (mvp behaviour)
      return [...prev, { ...asset, advertiser_id: selectedAdvertiserIds[0] || '' }];
    });
  };

  const allAssets = [...assets.catalogs, ...assets.pixels, ...assets.ttAccounts];

  const handleSelectAllAssets = () => {
    if (selectedAssets.length === allAssets.length) {
      setSelectedAssets([]);
    } else {
      const advertiserId = selectedAdvertiserIds[0] || '';
      setSelectedAssets(allAssets.map((a) => ({ ...a, advertiser_id: advertiserId })));
    }
  };

  // --- Fetch advertisers ---
  const fetchAdvertisers = async () => {
    setLoadingAdvertisers(true);
    setAdvertiserError(null);
    try {
      const data = await apiFetch<{ advertisers: Advertiser[]; error?: string }>(
        '/api/tiktok/advertisers',
      );
      if (data.error) {
        setAdvertiserError(data.error);
        return;
      }
      const advs = data.advertisers || [];
      setAdvertisers(advs);
      // Pre-check the stored selection (or everything on first connect).
      const preselected = advs.filter((a) => a.selected).map((a) => a.advertiser_id);
      setSelectedAdvertiserIds(
        preselected.length > 0 ? preselected : advs.map((a) => a.advertiser_id),
      );
    } catch (err) {
      setAdvertiserError(err instanceof Error ? err.message : 'Failed to load advertisers');
    } finally {
      setLoadingAdvertisers(false);
    }
  };

  // --- Fetch assets ---
  const fetchAssets = async () => {
    if (selectedBcIds.length === 0) return;

    setLoadingAssets(true);
    setAssetFetchError(null);
    try {
      const bcId = selectedBcIds[0]; // Use first selected BC (mvp behaviour)
      const data = await apiFetch<{
        catalogs: Asset[];
        pixels: Asset[];
        ttAccounts: Asset[];
        error?: string;
      }>(`/api/tiktok/assets?bc_id=${encodeURIComponent(bcId)}`);
      if (data.error) {
        setAssetFetchError(data.error);
      } else {
        setAssets({
          catalogs: data.catalogs || [],
          pixels: data.pixels || [],
          ttAccounts: data.ttAccounts || [],
        });
      }
    } catch {
      setAssetFetchError('Failed to load assets. You can skip this step.');
    } finally {
      setLoadingAssets(false);
    }
  };

  // --- Navigation ---
  const handleNext = () => {
    if (currentStep === 1) {
      void fetchAdvertisers();
      setCurrentStep(2);
    } else if (currentStep === 2) {
      void fetchAssets();
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    setCurrentStep((s) => Math.max(1, s - 1));
  };

  const save = async (withAssets: boolean) => {
    setSaving(true);
    setSaveError('');
    try {
      await apiPost('/api/tiktok/select-advertisers', {
        advertiser_ids: selectedAdvertiserIds,
        business_centers: selectedBcIds.map((id) => {
          const bc = businessCenters.find((b) => b.bc_id === id);
          return { bc_id: id, bc_name: bc?.bc_name || '' };
        }),
        assets: withAssets ? selectedAssets : [],
      });
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to connect TikTok');
      setSaving(false);
    }
  };

  const handleConfirm = () => void save(true);
  const handleSkipAssets = () => void save(false);

  const canNext = () => {
    // Don't dead-end accounts without Business Centers — the audit only
    // requires advertisers.
    if (currentStep === 1) return businessCenters.length === 0 || selectedBcIds.length > 0;
    if (currentStep === 2) return selectedAdvertiserIds.length > 0;
    return true;
  };

  const getSelectionCount = () => {
    if (currentStep === 1)
      return `${selectedBcIds.length} BC${selectedBcIds.length !== 1 ? 's' : ''} selected`;
    if (currentStep === 2)
      return `${selectedAdvertiserIds.length} advertiser${selectedAdvertiserIds.length !== 1 ? 's' : ''} selected`;
    return `${selectedAssets.length} asset${selectedAssets.length !== 1 ? 's' : ''} selected`;
  };

  const renderCheckboxRow = ({
    key,
    checked,
    onToggle,
    title,
    subtitle,
  }: {
    key: string;
    checked: boolean;
    onToggle: () => void;
    title: string;
    subtitle: string;
  }) => (
    <label
      key={key}
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors',
        checked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="size-4 shrink-0 accent-primary"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{title}</p>
        <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
      </div>
    </label>
  );

  const renderSpinner = (text: string) => (
    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
      <Loader2 className="mb-3 size-8 animate-spin text-foreground" />
      <p className="text-sm">{text}</p>
    </div>
  );

  const renderAssetSection = (title: string, items: Asset[]) => {
    if (items.length === 0) return null;
    return (
      <div className="mb-4">
        <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </p>
        <div className="space-y-2">
          {items.map((asset) =>
            renderCheckboxRow({
              key: assetKey(asset),
              checked: isAssetSelected(asset),
              onToggle: () => handleAssetToggle(asset),
              title: asset.asset_name,
              subtitle: `ID: ${asset.asset_id}`,
            }),
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <img
              src="/logos/tiktok_logo.svg"
              alt="TikTok"
              className="size-8 shrink-0 rounded object-contain"
            />
            <div>
              <DialogTitle>{STEP_TITLES[currentStep - 1]}</DialogTitle>
              <DialogDescription>
                Step {currentStep} of 3{currentStep === 3 ? ' · Optional' : ''}
              </DialogDescription>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="mt-3 flex gap-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={cn(
                  'h-1 flex-1 rounded-full transition-colors',
                  step <= currentStep ? 'bg-primary' : 'bg-muted',
                )}
              />
            ))}
          </div>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto pr-1">
          {/* Step 1: Business Centers */}
          {currentStep === 1 && (
            <div>
              {loadingBcs ? (
                renderSpinner('Loading Business Centers...')
              ) : businessCenters.length === 0 ? (
                <p className="rounded-lg bg-muted/50 py-4 text-center text-sm text-muted-foreground">
                  No Business Centers found — you can continue to advertiser selection.
                </p>
              ) : (
                <>
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold">
                      Business Centers ({businessCenters.length})
                    </h3>
                    <button
                      type="button"
                      onClick={handleSelectAllBcs}
                      className="text-xs font-medium text-foreground hover:text-muted-foreground"
                    >
                      {selectedBcIds.length === businessCenters.length
                        ? 'Deselect All'
                        : 'Select All'}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {businessCenters.map((bc) =>
                      renderCheckboxRow({
                        key: bc.bc_id,
                        checked: selectedBcIds.includes(bc.bc_id),
                        onToggle: () => handleBcToggle(bc.bc_id),
                        title: bc.bc_name,
                        subtitle: `ID: ${bc.bc_id}`,
                      }),
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Advertisers */}
          {currentStep === 2 && (
            <div>
              {loadingAdvertisers ? (
                renderSpinner('Loading advertisers...')
              ) : advertiserError ? (
                <p className="py-8 text-center text-sm text-destructive">{advertiserError}</p>
              ) : advertisers.length === 0 ? (
                <p className="rounded-lg bg-muted/50 py-4 text-center text-sm text-muted-foreground">
                  No advertisers found
                </p>
              ) : (
                <>
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Advertisers ({advertisers.length})</h3>
                    <button
                      type="button"
                      onClick={handleSelectAllAdvertisers}
                      className="text-xs font-medium text-foreground hover:text-muted-foreground"
                    >
                      {selectedAdvertiserIds.length === advertisers.length
                        ? 'Deselect All'
                        : 'Select All'}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {advertisers.map((adv) =>
                      renderCheckboxRow({
                        key: adv.advertiser_id,
                        checked: selectedAdvertiserIds.includes(adv.advertiser_id),
                        onToggle: () => handleAdvertiserToggle(adv.advertiser_id),
                        title: adv.advertiser_name || 'Unnamed Account',
                        subtitle: `ID: ${adv.advertiser_id}`,
                      }),
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3: Assets */}
          {currentStep === 3 && (
            <div>
              {loadingAssets ? (
                renderSpinner('Loading assets for selected Business Center...')
              ) : assetFetchError ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-muted-foreground">{assetFetchError}</p>
                </div>
              ) : allAssets.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-foreground">
                    No assets found for the selected Business Center.
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    You can skip this step and add assets later.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Available Assets ({allAssets.length})</h3>
                    <button
                      type="button"
                      onClick={handleSelectAllAssets}
                      className="text-xs font-medium text-foreground hover:text-muted-foreground"
                    >
                      {selectedAssets.length === allAssets.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  {renderAssetSection('Catalogs', assets.catalogs)}
                  {renderAssetSection('Pixels', assets.pixels)}
                  {renderAssetSection('TikTok Accounts', assets.ttAccounts)}
                </>
              )}
            </div>
          )}
        </div>

        {saveError && <p className="text-sm text-destructive">{saveError}</p>}

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
          <p className="text-sm text-muted-foreground">{getSelectionCount()}</p>
          <div className="flex gap-2">
            {currentStep === 1 ? (
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
            ) : (
              <Button variant="outline" onClick={handleBack} disabled={saving}>
                Back
              </Button>
            )}

            {currentStep === 3 ? (
              <>
                <Button variant="outline" onClick={handleSkipAssets} disabled={saving}>
                  Skip
                </Button>
                <Button onClick={handleConfirm} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    'Connect'
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={handleNext} disabled={!canNext() || saving || loadingBcs}>
                Next
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
